import React from 'react';
import { useWxbFieldIds } from '../_internal/fieldA11y';
import './Slider.css';

export interface WxbSliderProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'defaultValue' | 'max' | 'min' | 'onChange' | 'step' | 'type' | 'value'
> {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number, event: React.ChangeEvent<HTMLInputElement>) => void;
  showValue?: boolean;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const WxbSlider = React.forwardRef<HTMLInputElement, WxbSliderProps>(({
  min = 0,
  max = 100,
  step = 1,
  value: controlledValue,
  defaultValue = 0,
  onChange,
  disabled = false,
  className = '',
  showValue = true,
  id,
  'aria-describedby': ariaDescribedBy,
  ...inputProps
}, forwardedRef) => {
  const effectiveMin = Number.isFinite(min) ? min : 0;
  const requestedMax = Number.isFinite(max) ? max : effectiveMin;
  const effectiveMax = Math.max(effectiveMin, requestedMax);
  const initialValue = Number.isFinite(defaultValue) ? defaultValue : effectiveMin;
  const [internal, setInternal] = React.useState(() => clamp(initialValue, effectiveMin, effectiveMax));
  const rawValue = controlledValue !== undefined ? controlledValue : internal;
  const finiteValue = Number.isFinite(rawValue) ? rawValue : effectiveMin;
  const value = clamp(finiteValue, effectiveMin, effectiveMax);
  const span = effectiveMax - effectiveMin;
  const percent = span > 0 ? ((value - effectiveMin) / span) * 100 : 0;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const { controlId } = useWxbFieldIds(id);
  const valueId = `${controlId}-value`;

  React.useEffect(() => {
    rootRef.current?.style.setProperty('--wxb-slider-percent', `${percent}%`);
  }, [percent]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(event.target.value);
    if (controlledValue === undefined) setInternal(nextValue);
    onChange?.(nextValue, event);
  };

  return (
    <div ref={rootRef} className={`wxb-slider ${disabled ? 'is-disabled' : ''} ${className}`}>
      <div className="wxb-slider-track-wrap">
        <div className="wxb-slider-track" aria-hidden="true">
          <div className="wxb-slider-fill" />
        </div>
        <input
          {...inputProps}
          ref={forwardedRef}
          id={controlId}
          type="range"
          className="wxb-slider-input"
          min={effectiveMin}
          max={effectiveMax}
          step={step}
          value={value}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          onChange={handleChange}
        />
      </div>
      {showValue && (
        <output id={valueId} className="wxb-slider-value" htmlFor={controlId}>
          {value}
        </output>
      )}
    </div>
  );
});

WxbSlider.displayName = 'WxbSlider';
